import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Card, CardBody, CardHeader, Container, Modal, ModalBody, ModalFooter, ModalHeader, Form, FormGroup, Input, Label, Row, Col, Spinner, Badge, DropdownItem, DropdownMenu, DropdownToggle, Dropdown } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Download, Edit, MoreVertical, SlidersHorizontal, Trash } from "lucide-react";
import Select from "react-select";
import Flatpickr from "react-flatpickr"
import profileDefault from "../../../assets/images/icons/profile.png"
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const StaffList = () => {
  const [staffs, setStaffs] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStaffs, setFilteredStaffs] = useState([]);
  const [exportModal, setExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');
  const [exportLoading, setExportLoading] = useState(false);

  const toast = useRef(null);

  // Form state with all keys defined
  const [formData, setFormData] = useState({
    profile_picture: "",
    name: "",
    email: "",
    phone: "",
    type: "",
    gender: "",
    date_of_birth: "",
    department_id: "",
    designation_id: "",
    join_date: "",
    marital_status: "",
    blood_group: "",
    country: "",
    state: "",
    city: "",
    address: "",
    emergency_contact: "",
    education: "",
    experience: "",
    bank_name: "",
    bank_acc_number: "",
    bank_ifsc: "",
    team_position: ""
  });

  // Dropdown data
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const toggleDropdown = (id) => setDropdownOpen(dropdownOpen === id ? null : id);



  const toggle = () => {
    // When closing modal, reset selectedStaff and formData to default values.
    if (modal) {
      setSelectedStaff(null);
      setFormData({
        profile_picture: "",
        name: "",
        email: "",
        phone: "",
        type: "",
        gender: "",
        date_of_birth: "",
        department_id: "",
        designation_id: "",
        join_date: "",
        marital_status: "",
        blood_group: "",
        country: "",
        state: "",
        city: "",
        address: "",
        emergency_contact: "",
        education: "",
        experience: "",
        bank_name: "",
        bank_acc_number: "",
        bank_ifsc: "",
        team_position: ""
      });
    }
    setModal(!modal);
  };

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);


  const breadcrumbItems = [
    { title: "HR", link: "/" },
    { title: "Staffs", link: "#!" },
  ];

  const fetchStaffs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/staffs/list`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      if (response.data && response.data.status === "success") {
        setStaffs(response.data.data);
        setTotalRecords(response.data.data.length);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      console.error("Error fetching staffs:", error);
    }
    setLoading(false);
  }, []);

  // Fetch dropdown data
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/departments/list`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      setDepartments(response.data.data || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/designations/list`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      setDesignations(response.data.data || []);
    } catch (error) {
      console.error("Error fetching designations:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/countries`);
      setCountries(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const sortedCountries = [
    ...countries.filter(country => country.name === "India"),
    ...countries.filter(country => country.name !== "India")
  ];

  const fetchStates = async (countryId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/states/${countryId}`);
      setStates(response.data.data || []);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };


  const fetchCities = async (stateId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cities/${stateId}`);
      setCities(response.data.data || []);
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  useEffect(() => {
    fetchStaffs();
    fetchDepartments();
    fetchDesignations();
    fetchCountries();
  }, [fetchStaffs]);

  useEffect(() => {
    let filtered = staffs;

    if (filterType) {
      filtered = filtered.filter(staff => staff.type === filterType);
    }

    if (filterDepartment) {
      filtered = filtered.filter(staff => staff.department_id === filterDepartment);
    }

    if (filterDesignation) {
      filtered = filtered.filter(staff => staff.designation_id === filterDesignation);
    }

    if (searchTerm) {
      filtered = filtered.filter(staff =>
        staff.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredStaffs(filtered);
    setTotalRecords(filtered.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterDepartment, filterDesignation, searchTerm, staffs]);


  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const handleExport = async () => {
    setExportLoading(true);

    try {
      // Prepare data for export
      const prepareExportData = (data) => {
        return data.map(staff => ({
          'Name': staff.name || '',
          'Email': staff.email || '',
          'Phone': staff.phone || '',
          'Department': departments.find(dept => dept.id === staff.department_id)?.name || '',
          'Designation': designations.find(des => des.id === staff.designation_id)?.name || '',
          'Country': staff.country || '',
          'State': staff.state || '',
          'City': staff.city || '',
          'Address': staff.address || '',
          'Join Date': new Date(staff.join_date).toLocaleDateString(),
          'Status': staff.status || ''
        }));
      };

      const dataToExport = prepareExportData(filteredStaffs);

      if (exportFormat === 'excel') {
        exportToExcel(dataToExport);
      } else if (exportFormat === 'csv') {
        exportToCSV(dataToExport);
      } else if (exportFormat === 'pdf') {
        exportToPDF(dataToExport);
      }

      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: `Staffs exported as ${exportFormat.toUpperCase()} successfully`,
        life: 3000
      });
    } catch (error) {
      console.error("Export error:", error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export staffs',
        life: 3000
      });
    } finally {
      setExportLoading(false);
      setExportModal(false);
    }
  };

  const exportToExcel = (data) => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Staffs");
    XLSX.writeFile(workbook, `staffs_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportToCSV = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `staffs_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = (data) => {
    const doc = new jsPDF('landscape');
    const date = new Date().toLocaleDateString();
    const time = new Date().toLocaleTimeString();

    // Add title and metadata
    doc.setFontSize(18);
    doc.text("Staff Management Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on ${date} at ${time}`, 14, 28);

    // Add summary statistics
    let yPos = 36;
    doc.setFont(undefined, 'bold');
    doc.text("Summary Statistics", 14, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 6;

    doc.text(`Total Staffs: ${data.length}`, 20, yPos);
    yPos += 6;

    // Add client details table
    doc.setFont(undefined, 'bold');
    doc.text("Staff Details", 14, yPos);
    yPos += 8;

    doc.autoTable({
      startY: yPos,
      head: [['Name', 'Email', 'Phone', 'Department', 'Designation', 'Country', 'State', 'City', 'Address', 'Join Date', 'Status']],
      body: data.map(staff => [
        staff.Name,
        staff.Email,
        staff.Phone,
        staff.Department,
        staff.Designation,
        staff.Country,
        staff.State,
        staff.City,
        staff.Address,
        new Date(staff.JoinDate).toLocaleDateString(),
        staff.Status
      ]),
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 30 },
        2: { cellWidth: 20 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
        7: { cellWidth: 25 },
        8: { cellWidth: 30 },
        9: { cellWidth: 25 },
        10: { cellWidth: 'auto' }
      },
      margin: { top: 25, right: 10, left: 10 },
      didDrawPage: function (data) {
        // Add page number at the bottom
        doc.setFontSize(8);
        doc.text(
          'Page ' + doc.internal.getCurrentPageInfo().pageNumber,
          data.settings.margin.left,
          doc.internal.pageSize.height - 10
        );
      }
    });

    // Save the PDF
    doc.save(`staff_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Helper function to get status colors
  //   const getStatusColor = (status) => {
  //       switch(status) {
  //           case 'Active': return [40, 167, 69]; // Green
  //           case 'Inactive': return [108, 117, 125]; // Gray
  //           default: return [248, 249, 250]; // Light gray
  //       }
  //   };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedStaff) {
        // Ensure we're not accidentally overriding the email
        const updateData = { ...formData };

        if (formData.email !== selectedStaff.email) {
          updateData.email = formData.email;
        } else {
          delete updateData.email; // If email is unchanged, don't send it
        }

        await axios.post(
          `${API_BASE_URL}/staffs/update/${selectedStaff.id}`,
          updateData,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast.current.show({
          severity: "success",
          summary: "Updated",
          detail: "Staff updated successfully",
          life: 3000
        });
      } else {
        // Adding a new staff
        await axios.post(
          `${API_BASE_URL}/staffs/add`,
          formData,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast.current.show({
          severity: "success",
          summary: "Added",
          detail: "Staff added successfully",
          life: 3000
        });
      }

      toggle();
      fetchStaffs();
    } catch (error) {
      if (error.response && error.response.data.errors) {
        toast.current.show({
          severity: "error",
          summary: "Validation Error",
          detail: error.response.data.errors.email
            ? error.response.data.errors.email[0] // Show specific email error
            : "Failed to save staff",
          life: 3000
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to save staff",
          life: 3000
        });
      }
    }

    setLoading(false);
  };

  const handleDelete = async () => {
    if (!staffToDelete) return;
    try {
      await axios.delete(`${API_BASE_URL}/staffs/delete/${staffToDelete.id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      toast.current.show({ severity: "success", summary: "Deleted", detail: "Staff deleted successfully", life: 3000 });
      fetchStaffs();
    } catch (error) {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to delete Staff", life: 3000 });
    }
    setStaffToDelete(null);
    toggleDeleteModal();
  };


  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Staffs" breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0 flex-grow-1">Staff List</h4>
              <div className="">
                <Button color="success" onClick={() => setShowFilters(!showFilters)} className="me-2">
                  <SlidersHorizontal size={13} /> Filter
                </Button>
                <Button color="primary" onClick={toggle} className="me-2"><i className="mdi mdi-plus mr-1"></i> Add Staff</Button>
                <Button color="info" onClick={() => setExportModal(true)}>
                  <Download size={14} className="me-1" />Export Staffs
                </Button>
              </div>
            </CardHeader>

            {/* Filter Section */}
            {showFilters && (
              <CardBody>
                <Row className="mb-3">
                  <Col md={3}>
                    <FormGroup>
                      <Label>Search Name</Label>
                      <Input
                        type="text"
                        placeholder="Search by name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Type</Label>
                      <Input
                        type="select"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                      >
                        <option value="">All Types</option>
                        <option value="admin">Admin</option>
                        <option value="staff">Staff</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Department</Label>
                      <Input
                        type="select"
                        value={filterDepartment}
                        onChange={(e) => setFilterDepartment(e.target.value)}
                      >
                        <option value="">All Departments</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Designation</Label>
                      <Input
                        type="select"
                        value={filterDesignation}
                        onChange={(e) => setFilterDesignation(e.target.value)}
                      >
                        <option value="">All Designations</option>
                        {designations.map(des => (
                          <option key={des.id} value={des.id}>{des.name}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            )}

            <CardBody>
              <DataTable value={filteredStaffs.slice(first, first + rows)} loading={loading} stripedRows>
                {/* SI No Column */}
                <Column
                  header="SI No"
                  body={(rowData, options) => first + options.rowIndex + 1} // Generates serial numbers dynamically
                />
                {/* Profile Picture Column */}
                <Column
                  header=""
                  body={(rowData) => (
                    <img
                      src={rowData.profile_pic || profileDefault} // Fallback placeholder image
                      alt="Profile"
                      className="rounded-circle avatar-sm"
                    />
                  )}
                />



                <Column field="name" header="Name" sortable />
                <Column field="email" header="Email" sortable />
                <Column field="phone" header="Phone" sortable />
                <Column
                  field="type"
                  header="Type"
                  body={(rowData) => (
                    <Badge
                      color={
                        rowData.type === "admin"
                          ? "success"
                          : rowData.type === "teacher"
                            ? "primary"
                            : "secondary"
                      }
                    >
                      {rowData.type}
                    </Badge>
                  )}
                  sortable
                />
                <Column field="department" header="Department" sortable />
                <Column field="designation" header="Designation" sortable />

                {/* Actions Column */}
                <Column
                  header="Actions"
                  body={(rowData) => (
                    <Dropdown
                      isOpen={dropdownOpen === rowData.id}
                      toggle={() => toggleDropdown(rowData.id)}
                    >
                      <DropdownToggle tag="button" className="btn btn-light">
                        <MoreVertical size={20} />
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem
                          onClick={() => {
                            setSelectedStaff(rowData);
                            setFormData({
                              profile_picture: rowData.profile_picture || "",
                              name: rowData.name || "",
                              email: rowData.email || "",
                              phone: rowData.phone || "",
                              type: rowData.type || "",
                              gender: rowData.gender || "",
                              date_of_birth: rowData.date_of_birth || "",
                              department_id: rowData.department_id || "",
                              designation_id: rowData.designation_id || "",
                              join_date: rowData.join_date || "",
                              marital_status: rowData.marital_status || "",
                              blood_group: rowData.blood_group || "",
                              country: rowData.country || "",
                              state: rowData.state || "",
                              city: rowData.city || "",
                              address: rowData.address || "",
                              emergency_contact: rowData.emergency_contact || "",
                              education: rowData.education || "",
                              experience: rowData.experience || "",
                              bank_name: rowData.bank_name || "",
                              bank_acc_number: rowData.bank_acc_number || "",
                              bank_ifsc: rowData.bank_ifsc || "",
                              team_position: rowData.team_position || "",
                              // ... include all other fields from original code
                            });

                            if (rowData.country) fetchStates(rowData.country);
                            if (rowData.state) fetchCities(rowData.state);
                            toggle();
                          }}
                        >
                          <Edit size={16} className="me-2" />
                          Edit
                        </DropdownItem>

                        <DropdownItem className="text-danger">
                          <Link to={`/admin/office/staff-portfolio/${rowData.user_id}`} className="text-primary">
                            <Edit size={16} className="me-2" />
                            Create/Edit Portfolio
                          </Link>
                        </DropdownItem>

                        <DropdownItem
                          onClick={() => {
                            setStaffToDelete(rowData);
                            toggleDeleteModal();
                          }}
                          className="text-danger"
                        >
                          <Trash size={16} className="me-2" />
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                />
              </DataTable>
              <Paginator first={first} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} rowsPerPageOptions={[5, 10, 25, 50]} />
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={modal} centered toggle={toggle} size="xl" style={{ maxHeight: "90vh", overflowY: "auto" }}>
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={toggle}>{selectedStaff ? "Edit Staff" : "Add Staff"}</ModalHeader>
          <ModalBody>
            <Row>

              {/* Standard text inputs */}
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Name</Label>
                  <Input type="text" value={formData.name} placeholder="Enter name" onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} placeholder="Enter email" onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Phone</Label>
                  <Input type="text" value={formData.phone} placeholder="Enter phone number" onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label className="form-label" htmlFor="date_of_birth">
                    Date of Birth *
                  </Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control d-block"
                      placeholder="date of birth"
                      options={{
                        altInput: true,
                        altFormat: "F j, Y",
                        dateFormat: "Y-m-d",
                      }}
                      value={formData.date_of_birth}
                      onChange={(selectedDates) => {
                        const date = selectedDates[0];
                        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        setFormData({ ...formData, date_of_birth: formattedDate });
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Country</Label>
                  <Select
                    options={sortedCountries.map(country => ({
                      value: country.id,
                      label: country.name
                    }))}
                    value={sortedCountries.find(country => country.id === formData.country)
                      ? { value: formData.country, label: sortedCountries.find(country => country.id === formData.country).name }
                      : null}
                    onChange={(selectedOption) => {
                      setFormData({
                        ...formData,
                        country: selectedOption ? selectedOption.value : "",
                        state: "",
                        city: ""
                      });
                      if (selectedOption) {
                        fetchStates(selectedOption.value);
                      }
                    }}
                    isClearable
                    placeholder="Select Country"
                  />
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>State</Label>
                  <Input
                    type="select"
                    value={formData.state}
                    onChange={(e) => {
                      setFormData({ ...formData, state: e.target.value, city: "" });
                      fetchCities(e.target.value);
                    }}

                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state.id} value={state.id}>{state.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>City</Label>
                  <Input
                    type="select"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}

                  >
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>{city.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Address</Label>
                  <Input type="text" value={formData.address} placeholder="Enter address" onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Gender</Label>
                  <Input type="select" value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} required>
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="others">Other</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Marital Status</Label>
                  <Input type="select" value={formData.marital_status} onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}>
                    <option value="">Select Marital Status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </Input>
                </FormGroup>
              </Col>

              {/* Dropdown fields */}
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Department</Label>
                  <Input
                    type="select"
                    value={formData.department_id}
                    onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Designation</Label>
                  <Input
                    type="select"
                    value={formData.designation_id}
                    onChange={(e) => setFormData({ ...formData, designation_id: e.target.value })}
                    required
                  >
                    <option value="">Select Designation</option>
                    {designations.map(des => (
                      <option key={des.id} value={des.id}>{des.name}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Type</Label>
                  <Input
                    type="select"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Blood Group</Label>
                  <Input
                    type="select"
                    value={formData.blood_group}
                    onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}

                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </Input>
                </FormGroup>
              </Col>


              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Profile Picture</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFormData({ ...formData, profile_picture: file });
                      }
                    }}
                  />
                </FormGroup>
              </Col>

              {/* Preview uploaded image */}
              {formData.profile_picture && (
                <Col md={3} className="mb-3">
                  <img
                    src={URL.createObjectURL(formData.profile_picture)}
                    alt="Profile Preview"
                    className="img-fluid rounded-circle"
                    style={{ width: "100px", height: "100px", objectFit: "cover" }}
                  />
                </Col>
              )}
              <Col md={3} className="mb-3">
                <FormGroup>
                  <Label>Position</Label>
                  <Input type="text" value={formData.team_position} placeholder="Enter your position" onChange={(e) => setFormData({ ...formData, team_position: e.target.value })} required />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggle}>Cancel</Button>
            <Button color="primary" type="submit" disabled={loading}>
              {selectedStaff ? "Update" : "Add"}
              {loading && <Spinner className="ms-1" size="sm" >Loading...</Spinner>}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={exportModal} centered toggle={() => setExportModal(false)}>
        <ModalHeader toggle={() => setExportModal(false)}>Export Staffs</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Export Format</Label>
              <Input
                type="select"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
              >
                <option value="excel">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
                <option value="pdf">PDF (.pdf)</option>
              </Input>
            </FormGroup>

            <div className="mt-3 alert alert-info">
              <small>
                <strong>Note:</strong> The export will include all visible columns in the table.
                For PDF exports, some columns may be abbreviated to fit the page.
              </small>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setExportModal(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? 'Exporting...' : 'Export'}
            {exportLoading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this department?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
          <Button color="danger" onClick={handleDelete}>Delete</Button>
        </ModalFooter>
      </Modal>
      <Toast ref={toast} />
    </React.Fragment>
  );
};

export default StaffList;